// Policy Generation service page.
import { ArrowRight } from "lucide-react";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import InnerHero from "../../components/InnerHero";
import Reveal from "../../components/Reveal";
import { VIDEOS } from "../../lib/media";

export default function PolicyGeneration() {
  return (
    <div className="bg-black">
      <Nav route="inner" />
      <InnerHero
        video={VIDEOS.philosophy}
        eyebrow="Services · Governance"
        headline="Policies generated, not"
        italicWord="assembled."
        lede="We build the governance documentation your business needs to meet security, AI and compliance obligations."
      />

      <section className="bg-black py-20 md:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="text-white/40 text-xs tracking-widest uppercase mb-3">What this is</div>
            <h2 className="font-instrument text-3xl md:text-4xl text-white tracking-tight">
              Governance that keeps pace with the standards.
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-6">
              Security policies, AI usage policies, data governance frameworks, vendor risk registers.
              These are not static documents. Standards evolve, regulations change and auditors expect
              evidence of ongoing compliance.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-5">
              We generate your initial policy suite using AI, tailored to your industry and your
              specific obligations. Then we maintain and update them as standards change.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-black py-20 md:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="text-white/40 text-xs tracking-widest uppercase mb-3">How we work</div>
            <h2 className="font-instrument text-3xl md:text-4xl text-white tracking-tight">
              Policy suite delivered in two weeks.
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-6">
              We start with a scoping session to understand your obligations, your existing documentation
              and your customer requirements. From there we generate a full policy suite tailored to your
              sector.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-5">
              Policies are reviewed by a senior engineer before delivery. We include an evidence trail
              showing how each policy maps to the relevant standard or framework.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-black py-20 md:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="text-white/40 text-xs tracking-widest uppercase mb-3">Right for you if</div>
            <h2 className="font-instrument text-3xl md:text-4xl text-white tracking-tight">
              You need documentation for procurement, not the drawer.
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-6">
              You have a supplier questionnaire to answer and no existing policy framework. Your customer
              has asked for an AI usage policy and you are not sure what it should cover.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-5">
              You want policies that are practical, maintained and genuinely reflect how your business
              operates.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-black px-6 pb-24">
        <div className="max-w-2xl mx-auto liquid-glass rounded-3xl p-10 text-center">
          <h3 className="font-instrument text-3xl text-white">Ready to build something that lasts?</h3>
          <p className="text-white/60 text-base mt-4">
            Start with a 30-minute discovery call. No slide deck. Just an engineer.
          </p>
          <a
            href="/#contact"
            className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-3 bg-white text-black text-sm font-medium hover:bg-white/90 transition-colors"
          >
            Book a discovery <ArrowRight size={16} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
